import { useCallback, useEffect, useMemo, useState } from 'react'
import _isEmpty from 'lodash/isEmpty'
import _cloneDeep from 'lodash/cloneDeep'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FileRejection, useDropzone } from 'react-dropzone'

import {
  FILE_COUNT_ERROR_CODE,
  FILE_SIZE_ERROR_CODE,
  MB,
  POST_SUPPORTED_FILES_TYPES,
  POST_SUPPORTED_IMAGES_TYPES,
  POST_SUPPORTED_VIDEO_TYPES,
} from 'utils/constants'
import { FixMeLater } from 'types'
import axiosApi from 'app/axiosApi'
import { ERRORS } from 'locales/locales'
import { ICustomFile, IFiles } from 'types/file.types'
import { newsFeedActions } from 'app/slices/news-feed.slice'
import { useEditPostMutation } from 'app/services/post.service'
import { geValueByfullname, getInputValueByfullname } from 'utils/helpers/messageHelper'

import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import { useAppSelector } from 'app/hooks'
import { getParseMentionedUsers } from 'utils/hooks/useMessageInput'

export interface IMessageInputState {
  text: string
  value?: string
  mentionedUsers: string[]
  files: IFiles
}

const isEmpty = (str: string) => !str.trim().length

const inputDataValidation = (
  text = '',
  files?: IFiles,
  header = '',
  isComment?: boolean,
) => {
  if (isComment) return !isEmpty(text) || !_isEmpty(files)
  // Post düzenleme: hem başlık hem içerik zorunlu
  return !isEmpty(header) && !isEmpty(text)
}


const useEditPostInput = ({
  post,
  onClose,
  onEditComment,
  type,
}: {
  post: FixMeLater
  onClose: FixMeLater
  onEditComment?: FixMeLater
  type: 'photo' | 'video' | 'file'
}) => {
  const [isValid, setValid] = useState(false)
  const [editedContent, setEditedContent] = useState({
    ...post.content,
  })

  const [itHasComment, setHasComment] = useState<boolean>(post?.commentToggle ?? false)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [editPost] = useEditPostMutation()
  const accessToken = useAppSelector((state) => state.auth.accessToken)
  const systemData = useAppSelector((state) => state.system.data)

  const acceptableExtensions = useMemo(() => (systemData?.fileExtensions ?? []).map((extension: string) => extension.toLowerCase()), [systemData?.fileExtensions])

  const extensions = useCallback((type:FixMeLater) => {
    if (!acceptableExtensions || acceptableExtensions.length === 0) return {}
    const result: FixMeLater = {}
    Object.entries(type).forEach(([mimeType, extensions]) => {
      const matchingExtensions = acceptableExtensions.includes(mimeType)
      if (matchingExtensions) result[mimeType] = extensions
    })
    return result
  }, [acceptableExtensions])

  const acceptedType = useCallback((type: string) => {
    switch (type) {
      case 'photo':
        return extensions(POST_SUPPORTED_IMAGES_TYPES)
      case 'video':
        return extensions(POST_SUPPORTED_VIDEO_TYPES)
      case 'file':
        return extensions(POST_SUPPORTED_FILES_TYPES)
      default:
        return {}
    }
  }, [extensions])

  const onDropAccepted = (files: Array<File>) => {
    if (
      files.length > 4 ||
      editedContent.files.length > 3 ||
      files.length + editedContent.files.length > 4
    ) {
      return DropdownAlert.ref.show({
        isError: true,
        message: t(ERRORS.FILE_COUNT_ERROR, { count: 4 }),
      })
    }
    setEditedContent((prev: IMessageInputState) => {
      return {
        ...prev,
        files: [...prev.files, ...files],
      }
    })
  }

  const onEditedContentFileChange = (files: Array<File>) => {
    setEditedContent((prev: IMessageInputState) => {
      return {
        ...prev,
        files: [...prev.files, ...files],
      }
    })
  }

  const onDropRejected = (fileRejections: Array<FileRejection>) => {
    if (fileRejections.some((file: FileRejection) => file?.errors?.some((error: any) => error?.code === FILE_COUNT_ERROR_CODE))) {
      DropdownAlert.ref.show({
        isError: true,
        message: t(ERRORS.FILE_COUNT_ERROR, { count: 4 }),
      })
      throw new Error(t(ERRORS.FILE_COUNT_ERROR, { count: 4 }))
    }
    if (fileRejections.some((file: FileRejection) => file?.errors?.some((error: any) => error?.code === FILE_SIZE_ERROR_CODE))) {
      DropdownAlert.ref.show({
        isError: true,
        message: t(ERRORS.FILE_SIZE_ERROR, {
          fileSize: systemData?.fileSizeLimit,
          fileSizeType: "MB",
          file_name: fileRejections.map((file: FileRejection) => file?.file?.name)?.join(', '),
        }),
      })
      return
    }
  }

  const fileTypesNotAllowed = useCallback((fileTypes: string) => {
    DropdownAlert.ref.show({
      isError: true,
      message: t(ERRORS.UNSUPPORTED_FILE_TYPE_ERROR, {
        file_name: fileTypes,
      }),
    })
    throw new Error(t(ERRORS.UNSUPPORTED_FILE_TYPE_ERROR, {
      file_name: fileTypes,
    }))
  }, [t])

  const { inputRef, getInputProps } = useDropzone({
    accept: acceptedType(type),
    maxFiles: 4,
    maxSize: parseInt(systemData?.fileSizeLimit ?? 50) * MB,
    onDropAccepted,
    onDropRejected,
    onDrop: (_acceptedFiles, fileRejections) => {
      //TODO: Arapça eklenecek.
      if (Object.keys(acceptedType(type)).length === 0) {
        switch (type) {
          case 'photo':
            return fileTypesNotAllowed(t(ERRORS.PHOTO_FILE_TYPES_NOT_ALLOWED))
          case 'video':
            return fileTypesNotAllowed(t(ERRORS.VIDEO_FILE_TYPES_NOT_ALLOWED))
          case 'file':
            return fileTypesNotAllowed(t(ERRORS.NO_FILE_TYPES_ALLOWED))
          default:
            return fileTypesNotAllowed(t(ERRORS.NO_FILE_TYPES_ALLOWED))
        }
      }
      if (
        fileRejections?.length &&
        fileRejections?.some((file: FileRejection) =>
          file?.errors.some(
            (error: any) =>
              error?.code === FILE_COUNT_ERROR_CODE || error?.code === FILE_SIZE_ERROR_CODE,
          ),
        )
      ) {
        return onDropRejected(fileRejections)
      }
      if (fileRejections && fileRejections?.length) {
        DropdownAlert.ref.show({
          isError: true,
          message: t(ERRORS.UNSUPPORTED_FILE_TYPE_ERROR, {
            file_name: fileRejections?.map((file: FileRejection) => file?.file?.name)?.join(', '),
          }),
        })
      }
    },
  })

  const onCancelEditFile = (file: FixMeLater) => {
    const tempArray = editedContent.files
    const upload = tempArray.filter((item: ICustomFile) => item._id)
    const unupload = tempArray.filter((item: ICustomFile) => !item._id)
    if (file?._id) {
      const filtered = upload.filter((item: ICustomFile) => item._id !== file?._id)
      return setEditedContent({ ...editedContent, files: filtered.concat(unupload) })
    } else {
      const filtered = unupload.filter((item: File) => item.name !== file?.name)
      return setEditedContent({ ...editedContent, files: filtered.concat(upload) })
    }
  }

  const onChangeEditText = (text: string) => {
    const mentionedUsers = getParseMentionedUsers(text)
    setEditedContent((prevData: IMessageInputState) => ({ ...prevData, text, mentionedUsers }))
  }

  const onChangeHeaderInputData = (header: string) => {
    setEditedContent((prevData: IMessageInputState) => ({ ...prevData, header }))
  }

  const onChangeToggleComment= (commentToggle: FixMeLater) => {
    setHasComment(commentToggle)
  }

  const onEditEmojiClick = (emojiData: FixMeLater) => {
    setEditedContent((prevData: IMessageInputState) => ({
      ...prevData,
      text: prevData.text + emojiData.emoji,
    }))
  }

  const onEditPost = async () => {
    try {
  
      
      const { mentionedUsers, text, files = [], header  } = getInputValueByfullname(editedContent)

      
      const value = geValueByfullname(text, mentionedUsers, post.userData)


      const trimmedText = text?.trim() || ''
      const uploadedFiles = editedContent.files?.filter((item: ICustomFile) => item._id) || []
      const unuploadFiles = editedContent.files?.filter((item: ICustomFile) => !item._id) || []
      
 

      try {

        const params = {
          commentToggle: itHasComment,
          content: { mentionedUsers, text: trimmedText, value, files, header },
          postId: post?._id,
          status: 'pending',
        }
        dispatch(
          newsFeedActions.updatePost(params),
        )

        onClose()
        
        if (unuploadFiles?.length > 0) {
       
          dispatch(newsFeedActions.postLoading(true))
          const files = []
          try {
            const result = await axiosApi.uploadFile(
              `files/uploads?from=post&method=edit&teams=${encodeURIComponent(
                JSON.stringify([post.teamId]),
              )}`,
              unuploadFiles,
              'file',
              { params: params },
              undefined,
              accessToken,
            )
            if (result?.data?.success) {
              files.push(result?.data?.data)
           
            } else {
              console.error('File upload failed:', result)
            }
          } catch (error) {
            console.error('File upload error:', error)
          }

          // for (const file of unuploadFiles) {
          //   try {
          //     console.log('Uploading file:', file.name)
          //     const result = await axiosApi.uploadFile(`files/uploads?from=post&method=edit&params=${JSON.stringify(params)}&uploadedFiles=${JSON.stringify(uploadedFiles)}`, [file], 'files', undefined, accessToken)
          //     if (result?.data?.success) {
          //       files.push(result?.data?.data)
          //       console.log('File uploaded successfully:', file.name)
          //     } else {
          //       console.error('File upload failed:', file.name, result)
          //       DropdownAlert.ref.show({
          //         isError: true,
          //         message: t(ERRORS.UPLOAD_FILE_ERROR, {
          //           file_name: file.name,
          //         }),
          //       })
          //       return
          //     }
          //   } catch (fileError) {
          //     console.error('File upload error:', file.name, fileError)
          //     DropdownAlert.ref.show({
          //       isError: true,
          //       message: t(ERRORS.UPLOAD_FILE_ERROR, {
          //         file_name: file.name,
          //       }),
          //     })
          //     return
          //   }
          // }
          
        } else {

          try {
            const { data }: FixMeLater = await editPost({
              commentToggle: itHasComment,
              content: { mentionedUsers, text: trimmedText, value, files, header },
              _id: post?._id,
            })
            if (data.success) {
   
              if (onEditComment) {
                onEditComment((prev: FixMeLater) => {
                  const tempData = _cloneDeep(prev)
                  const index = prev.findIndex((item: FixMeLater) => item._id === data.data.postId)
                  tempData[index].content = { ...data?.data?.content }
                  return tempData
                })
                onClose()
              }
              dispatch(newsFeedActions.updatePost({ post: { ...data?.data, status: 'success' } }))
              dispatch(newsFeedActions.postLoading(false))
              onClose()
            } else {
              console.error('Edit post failed:', data)
            }
          } catch (editError) {
            console.error('Edit post error without files:', editError)
            throw editError
          }
        }
      } catch (dispatchError) {
        console.error('Dispatch error:', dispatchError)
        throw dispatchError
      }
    } catch (err) {
      console.error('onEditPost error:', err)
      console.error('Error details:', {
        message: (err as any)?.message,
        stack: (err as any)?.stack,
        editedContent,
        post: post?._id
      })
      
      try {
        dispatch(
          newsFeedActions.updatePost({
            post: {
              commentToggle: itHasComment,
              content: post.content,
              postId: post?._id,
              status: 'success',
            },
          }),
        )
        dispatch(newsFeedActions.postLoading(false))
      } catch (dispatchError) {
        console.error('Error in error handler dispatch:', dispatchError)
      }
    }
  }

  useEffect(() => {
    const valid = inputDataValidation(
      editedContent?.text,
      editedContent?.files,
      editedContent?.header ?? '',
      !!onEditComment,
    )
    setValid(valid)
  }, [editedContent, onEditComment])

  return {
    isValidEditPost: isValid,
    inputRef,
    getInputProps,
    onEditPost,
    onEditEmojiClick,
    onChangeEditText,
    onCancelEditFile,
    onEditedContentFileChange,
    onChangeHeaderInputData,
    onChangeToggleComment,
    editedContent,
  }
}

export default useEditPostInput
